import { create } from 'zustand'
import { generationsApi, pollGeneration, clearCache, CACHE_KEYS } from '../lib/api'
import type { Generation, GenerationDetail, PromptInputs, LightingStyle, AspectRatio, CameraAngle, LensStyle } from '../types'

interface GenerationState {
  // Generation list
  generations: Generation[]
  loadingList: boolean
  lastFetched: number | null
  isFullGalleryLoaded: boolean
  loadingFullGallery: boolean

  // Current generation
  currentGeneration: GenerationDetail | null
  isGenerating: boolean
  generationStep: string

  // Form inputs
  inputs: PromptInputs

  // Actions
  fetchGenerations: (projectId?: string) => Promise<void>
  forceRefresh: (projectId?: string) => Promise<void>
  loadFullGallery: (projectId?: string) => Promise<void>
  fetchGeneration: (id: string) => Promise<void>
  startGeneration: (projectId?: string, styleReferenceId?: string) => Promise<string>
  setInput: <K extends keyof PromptInputs>(key: K, value: PromptInputs[K]) => void
  resetInputs: () => void
  clearCurrentGeneration: () => void
  updateGeneration: (id: string, updates: Partial<Generation>) => void
  deleteGeneration: (id: string) => Promise<void>
}

const defaultInputs: PromptInputs = {
  productDescription: '',
  usageContext: '',
  backgroundStyle: '',
  lightingStyle: 'Soft, diffused light with gentle, blurry shadows' as LightingStyle,
  aspectRatio: '1:1 (Square)' as AspectRatio,
  cameraAngle: 'Three-quarter angle (45°)' as CameraAngle,
  lensStyle: 'Standard lens (35-50mm equivalent)' as LensStyle,
  extraMood: '',
  productImage: undefined,
  backgroundReference: undefined,
  autoMode: true,
  fiveShotMode: false,
  whiteBackgroundMode: false,
  freestyleMode: false,
}

export const useGenerationStore = create<GenerationState>((set, get) => ({
  generations: [],
  loadingList: false,
  lastFetched: null,
  isFullGalleryLoaded: false,
  loadingFullGallery: false,
  currentGeneration: null,
  isGenerating: false,
  generationStep: '',
  inputs: { ...defaultInputs },

  fetchGenerations: async (projectId?: string) => {
    const state = get()
    const now = Date.now()

    // Skip if we have data fetched within last 30 seconds
    if (state.generations.length > 0 && state.lastFetched && (now - state.lastFetched) < 30000) {
      return
    }

    try {
      set({ loadingList: true })
      const generations = await generationsApi.getAll(projectId)
      set({ generations, lastFetched: now })
    } catch (error) {
      console.error('Fetch generations error:', error)
    } finally {
      set({ loadingList: false })
    }
  },

  forceRefresh: async (projectId?: string) => {
    set({ lastFetched: null, isFullGalleryLoaded: false })
    await get().fetchGenerations(projectId)
  },

  loadFullGallery: async (projectId?: string) => {
    try {
      set({ loadingFullGallery: true })
      const generations = await generationsApi.getAll(projectId, 1000)
      set({ generations, loadingFullGallery: false, isFullGalleryLoaded: true, lastFetched: Date.now() })
    } catch (error) {
      console.error('Load full gallery error:', error)
      set({ loadingFullGallery: false })
    }
  },

  fetchGeneration: async (id: string) => {
    try {
      const detail = await generationsApi.getOne(id)
      set({ currentGeneration: detail })
    } catch (error) {
      console.error('Fetch generation error:', error)
    }
  },

  startGeneration: async (projectId?: string, styleReferenceId?: string) => {
    const { inputs } = get()

    try {
      set({
        isGenerating: true,
        generationStep: inputs.fiveShotMode
          ? 'Designing 5 unique angles...'
          : 'Creating your product photo...',
        currentGeneration: null,
      })

      // Start generation
      const result = await generationsApi.create({
        projectId,
        styleReferenceId,
        inputs,
      })

      // Poll for completion
      set({ generationStep: 'Generating images...' })

      const detail = await pollGeneration(result.id, (update) => {
        const step = inputs.fiveShotMode
          ? `Generated ${update.images.length} of 5 images...`
          : 'Finalizing...'
        set({ currentGeneration: update, generationStep: step })
      })

      set({
        currentGeneration: detail,
        isGenerating: false,
        generationStep: '',
      })

      // Clear user cache since credits were used
      clearCache(CACHE_KEYS.USER)
      clearCache(CACHE_KEYS.SUBSCRIPTION)

      // Refresh generations list
      try {
        await get().fetchGenerations(projectId)
      } catch (err) {
        console.error('Failed to refresh generations after creation:', err)
        // Non-fatal - generation still succeeded
      }

      return result.id
    } catch (error: any) {
      const { currentGeneration } = get()
      const hasPartialImages = (currentGeneration?.images?.length || 0) > 0

      set({
        isGenerating: false,
        generationStep: '',
        // Keep currentGeneration if we have partial images (so isPartial works)
        // Clear it entirely if we have 0 images (nothing to show)
        currentGeneration: hasPartialImages ? currentGeneration : null,
      })
      throw error
    }
  },

  setInput: (key, value) => {
    set((state) => ({
      inputs: { ...state.inputs, [key]: value },
    }))
  },

  resetInputs: () => {
    set({ inputs: { ...defaultInputs } })
  },

  clearCurrentGeneration: () => {
    set({ currentGeneration: null })
  },

  updateGeneration: (id: string, updates: Partial<Generation>) => {
    set((state) => ({
      generations: state.generations.map((gen) =>
        gen.id === id ? { ...gen, ...updates } : gen
      ),
    }))
  },

  deleteGeneration: async (id: string) => {
    await generationsApi.delete(id)
    set((state) => ({
      generations: state.generations.filter((gen) => gen.id !== id),
    }))
  },
}))
